import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBan, faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import stockpileService from '../../services/stockpileService';
import './StockMovementModal.css';

const TYPE_LABELS = {
    opening_balance: 'Opening',
    inbound: 'Inbound',
    outbound: 'Outbound',
    adjustment: 'Adjustment',
    void: 'Void',
};

const SOURCE_LABELS = {
    manual: 'Manual',
    opening: 'Initial',
    delivery: 'Delivery',
    invoice: 'Invoice',
};

const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 }).replace(/,/g, ' ');
};

const formatDateTime = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

const unitDisplayFor = (movement, fallback = '') =>
    movement?.unit_name || movement?.unit_code || fallback;

const VoidMovementModal = ({
    isOpen,
    movement,
    currentBalance,
    unitDisplay,
    onClose,
    onConfirmed,
}) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setReason('');
            setError(null);
            setSubmitting(false);
        }
    }, [isOpen, movement?.id]);

    const balanceAfter = useMemo(() => {
        if (currentBalance === null || currentBalance === undefined || !movement) return null;
        const current = Number(currentBalance);
        const delta = Number(movement.delta);
        if (Number.isNaN(current) || Number.isNaN(delta)) return null;
        return current - delta;
    }, [currentBalance, movement]);

    if (!isOpen || !movement) return null;

    const typeLabel = TYPE_LABELS[movement.movement_type] || movement.movement_type;
    const sourceLabel = SOURCE_LABELS[movement.source] || movement.source;
    const unitLabel = unitDisplayFor(movement, unitDisplay);
    const deltaSign = Number(movement.delta) >= 0 ? '+' : '−';
    const deltaMagnitude = formatNumber(Math.abs(Number(movement.delta)));
    const goesNegative = balanceAfter !== null && balanceAfter < 0;

    const handleConfirm = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            setError(null);
            await stockpileService.voidMovement(movement.id, reason.trim());
            onConfirmed?.();
        } catch (err) {
            console.error('Void failed', err);
            const detail = err?.response?.data;
            const detailString =
                typeof detail === 'string'
                    ? detail
                    : detail?.detail || detail?.error || JSON.stringify(detail || {});
            setError(detailString || 'Could not void this movement.');
        } finally {
            setSubmitting(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="stockpile-modal-overlay" onClick={submitting ? undefined : onClose}>
            <div className="stockpile-modal" onClick={(e) => e.stopPropagation()}>
                <div className="stockpile-modal-header outbound">
                    <h3 className="stockpile-modal-title">
                        <FontAwesomeIcon icon={faBan} />
                        Void stockpile movement
                    </h3>
                    <button
                        type="button"
                        className="stockpile-modal-close"
                        onClick={onClose}
                        disabled={submitting}
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <form className="hms-stacked-form" onSubmit={handleConfirm}>
                    <div className="stockpile-modal-body">
                        <div className="void-summary">
                            <div className="void-summary-row">
                                <span className="void-summary-label">Movement</span>
                                <span className="void-summary-value">{typeLabel} · {sourceLabel}</span>
                            </div>
                            <div className="void-summary-row">
                                <span className="void-summary-label">Quantity</span>
                                <span
                                    className={`void-summary-value void-delta ${
                                        Number(movement.delta) > 0 ? 'positive' : 'negative'
                                    }`}
                                >
                                    {deltaSign}{deltaMagnitude} {unitLabel}
                                </span>
                            </div>
                            <div className="void-summary-row">
                                <span className="void-summary-label">When</span>
                                <span className="void-summary-value">{formatDateTime(movement.occurred_at)}</span>
                            </div>
                            {movement.created_by_name && (
                                <div className="void-summary-row">
                                    <span className="void-summary-label">Posted by</span>
                                    <span className="void-summary-value">{movement.created_by_name}</span>
                                </div>
                            )}
                            {movement.notes && (
                                <div className="void-summary-row">
                                    <span className="void-summary-label">Notes</span>
                                    <span className="void-summary-value void-notes">{movement.notes}</span>
                                </div>
                            )}
                        </div>

                        {balanceAfter !== null && (
                            <div className={`void-impact${goesNegative ? ' negative' : ''}`}>
                                <div className="void-impact-line">
                                    <span>Current balance</span>
                                    <span className="void-impact-number">
                                        {formatNumber(currentBalance)} {unitLabel}
                                    </span>
                                </div>
                                <div className="void-impact-line">
                                    <span>After void</span>
                                    <span className="void-impact-number bold">
                                        {formatNumber(balanceAfter)} {unitLabel}
                                    </span>
                                </div>
                                {goesNegative && (
                                    <div className="void-impact-warning">
                                        <FontAwesomeIcon icon={faTriangleExclamation} />
                                        Voiding this entry will leave the stockpile balance negative.
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="void-explainer">
                            <FontAwesomeIcon icon={faTriangleExclamation} className="void-explainer-icon" />
                            <div>
                                <strong>This is append-only.</strong> The original movement is not deleted.
                                A reverse entry will be added so the audit trail stays intact, and both
                                rows remain visible in history.
                            </div>
                        </div>

                        <div className="stockpile-field">
                            <label className="stockpile-field-label">Reason (optional)</label>
                            <textarea
                                className="stockpile-textarea"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Why are you voiding this entry?"
                                maxLength={2000}
                            />
                        </div>

                        {error && <div className="stockpile-modal-error">{error}</div>}
                    </div>

                    <div className="stockpile-modal-footer">
                        <button
                            type="button"
                            className="stockpile-modal-btn secondary"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="stockpile-modal-btn danger"
                            disabled={submitting}
                        >
                            {submitting && <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.4rem' }} />}
                            Void movement
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default VoidMovementModal;
